import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockIcon from '@mui/icons-material/Lock';
import { useCallback } from 'react';
import { useUpdate } from 'react-admin';

import { Semaphore } from '@/screens/therapistAppConfig/Semaphore';
import { useGetAppConfig } from '@/screens/therapistAppConfig/useGetAppConfig';
import { useOnSettled } from '@/screens/therapistAppConfig/useOnSettled';

import { MatureChip } from './MatureChip';
import { BooleanFlagEntry, EnumFlagEntry, FlagEntry } from './types';

const readJsonObject = (record: unknown): Record<string, unknown> | null => {
  if (record === null || record === undefined) return null;
  if (typeof record === 'string') {
    try {
      const parsed: unknown = JSON.parse(record);
      return typeof parsed === 'object' &&
        parsed !== null &&
        !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (typeof record === 'object' && !Array.isArray(record)) {
    return record as Record<string, unknown>;
  }
  return null;
};

const readGlobalBooleanFromEntry = (
  remoteAppConfig: Record<string, unknown> | undefined,
  entry: BooleanFlagEntry,
): boolean => {
  if (!remoteAppConfig) return false;
  if (entry.globalObjectBooleanKey) {
    const parent = readJsonObject(remoteAppConfig[entry.globalField]);
    const v = parent?.[entry.globalObjectBooleanKey];
    return v === true || v === 1 || v === '1';
  }
  return !!remoteAppConfig[entry.globalField];
};

const buildBooleanTogglePayload = (
  remoteAppConfig: Record<string, unknown> | undefined,
  entry: BooleanFlagEntry,
  currentValue: boolean,
): Record<string, unknown> => {
  const next = !currentValue;
  if (entry.globalObjectBooleanKey) {
    const parentKey = entry.globalField;
    const childKey = entry.globalObjectBooleanKey;
    const existingRaw = remoteAppConfig?.[parentKey];
    const existingObj = readJsonObject(existingRaw) ?? {};
    return {
      [parentKey]: {
        ...existingObj,
        [childKey]: next ? 1 : 0,
      },
    };
  }
  return {
    [entry.globalField]: next ? 1 : 0,
  };
};

type Props = {
  entry: FlagEntry;
};

const Header = ({
  label,
  description,
  mature,
}: {
  label: string;
  description?: string;
  mature?: boolean;
}) => (
  <Box
    display="flex"
    alignItems="center"
    gap={0.5}
    marginBottom={1}
    flexWrap="wrap"
  >
    <Typography variant="subtitle1" fontWeight={600}>
      {label}
    </Typography>
    {description ? (
      <Tooltip title={description}>
        <IconButton size="small" sx={{ padding: 0.5 }}>
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null}
    {mature ? <MatureChip /> : null}
  </Box>
);

const BooleanRow = ({ entry }: { entry: BooleanFlagEntry }) => {
  const [updateConfig, { isLoading }] = useUpdate(
    'therapistAppConfig',
    { id: 'current' },
  );
  const { remoteAppConfig, refetchAppConfig } = useGetAppConfig();
  const { onSettled: doSettled } = useOnSettled();

  const onSettled = useCallback(async () => {
    doSettled();
    await refetchAppConfig();
  }, [doSettled, refetchAppConfig]);

  const remote = remoteAppConfig as Record<string, unknown> | undefined;

  const value = readGlobalBooleanFromEntry(remote, entry);
  const readOnlyLegacy = entry.adminGlobalReadOnly === true;
  const readOnlyMature = entry.stability === 'mature';
  const readOnlyInAdmin = readOnlyLegacy || readOnlyMature;
  const lockedByDesign =
    readOnlyInAdmin || (entry.lockOnceEnabled && value);
  const disabled = lockedByDesign || isLoading;

  const onToggle = useCallback(async () => {
    if (disabled) return;
    await updateConfig(
      'therapistAppConfig',
      {
        id: 'current',
        data: buildBooleanTogglePayload(remote, entry, value),
      },
      { onSettled },
    );
  }, [disabled, entry, onSettled, remote, updateConfig, value]);

  const tooltipText = readOnlyLegacy
    ? 'Legacy compatibility flag; the global control is read-only in admin.'
    : readOnlyMature
      ? 'Mature (shipped); the global control is read-only in admin.'
      : entry.lockOnceEnabled && value
        ? 'Locked while enabled. Disabling this flag is a destructive operation; ask an engineer if you really need to turn it off.'
        : entry.description;

  return (
    <Box>
      <Header
        label={entry.label}
        description={tooltipText}
        mature={readOnlyMature}
      />
      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
        <Box display="flex" alignItems="center" gap={0.5}>
          {lockedByDesign ? (
            <LockIcon
              sx={{ fontSize: '1rem', color: 'text.secondary' }}
            />
          ) : null}
          <Switch
            checked={value}
            onChange={onToggle}
            disabled={disabled}
          />
        </Box>
        <Box
          marginLeft="1rem"
          display="flex"
          flexDirection="row"
          alignItems="center"
        >
          {isLoading && <CircularProgress size={14} />}
          {!isLoading && <Semaphore enabled={value} />}
        </Box>
      </Box>
    </Box>
  );
};

const EnumRow = ({ entry }: { entry: EnumFlagEntry }) => {
  const [updateConfig, { isLoading }] = useUpdate(
    'therapistAppConfig',
    { id: 'current' },
  );
  const { remoteAppConfig, refetchAppConfig } = useGetAppConfig();
  const { onSettled: doSettled } = useOnSettled();

  const onSettled = useCallback(async () => {
    doSettled();
    await refetchAppConfig();
  }, [doSettled, refetchAppConfig]);

  const rawValue = remoteAppConfig?.[entry.globalField] as
    | string
    | undefined;
  const value =
    rawValue && entry.options.includes(rawValue)
      ? rawValue
      : entry.defaultOption;

  const readOnlyLegacy = entry.adminGlobalReadOnly === true;
  const readOnlyMature = entry.stability === 'mature';
  const readOnlyInAdmin = readOnlyLegacy || readOnlyMature;

  const onChange = useCallback(
    async (nextValue: string) => {
      if (readOnlyInAdmin) return;
      await updateConfig(
        'therapistAppConfig',
        {
          id: 'current',
          data: { [entry.globalField]: nextValue },
        },
        { onSettled },
      );
    },
    [entry.globalField, onSettled, updateConfig, readOnlyInAdmin],
  );

  const tooltipText = readOnlyLegacy
    ? 'Legacy compatibility flag; the global control is read-only in admin.'
    : readOnlyMature
      ? 'Mature (shipped); the global control is read-only in admin.'
      : entry.description;

  return (
    <Box>
      <Header
        label={entry.label}
        description={tooltipText}
        mature={readOnlyMature}
      />
      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
        <Box display="flex" alignItems="center" gap={0.5}>
          {readOnlyInAdmin ? (
            <LockIcon
              sx={{ fontSize: '1rem', color: 'text.secondary' }}
            />
          ) : null}
          <FormControl
            size="small"
            disabled={readOnlyInAdmin || isLoading}
            sx={{ minWidth: 220 }}
          >
            <InputLabel>Value</InputLabel>
            <Select
              value={value}
              label="Value"
              onChange={event => {
                void onChange(event.target.value);
              }}
            >
              {entry.options.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {isLoading && <CircularProgress size={14} />}
      </Box>
      <Box marginTop={1}>
        <Typography variant="body2" color="text.secondary">
          Current: {value}
        </Typography>
      </Box>
    </Box>
  );
};

export const FeatureFlagGlobalRow = ({ entry }: Props) => {
  if (entry.kind === 'boolean') {
    return <BooleanRow entry={entry} />;
  }
  return <EnumRow entry={entry} />;
};
